import React, { ReactNode, useEffect, useState } from "react";

import { castingSessionIdSelector } from "@redux/casting";
import { useCurrentSongSelector, saveCurrentSong } from "@redux/player";
import { waitingServiceWorkerSelector } from "@redux/serviceWorker";
import { debounce } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { useAsyncFn, useMedia } from "react-use";

import CastingButtonAndModal from "components/b2c/CastingButtonAndModal";
import CastingControlBar from "components/b2c/CastingControlBar";
import { MAX_TIMEOUT_DELAY_IN_MILLISECONDS } from "constants/timeout";
import { isMobile } from "services/helpers/browser";
import { getRemainingTimeInMilliseconds } from "services/helpers/formatTime";
import { useFreeTrialState } from "services/hooks/useFreeTrialState";
import { postCastingEvent } from "services/networking/casting";
import { getOffersMessageAndModal } from "services/networking/user";
import { PlayerEvent } from "shared-types/player-event";
import { breakpoints } from "stylesheetB2c";

import {
	BannersContainer,
	LayoutContainer,
	LayoutContent,
	NavigationArea,
	PlayerAreaContainer,
	StyledCookieBanner,
	StyledB2cPlayerArea,
} from "./WebPlayerLayout.style";
import { isOngoingOfferWithExpirationDate } from "./WebPlayerLayout.utils";
import BottomNavBar from "./components/BottomNavBar";
import DesktopModal from "./components/DesktopModal";
import Header from "./components/Header";
import MobileModal from "./components/MobileModal";
import NewAppBanner from "./components/NewAppBanner";

export interface Props {
	isPlayerHiddenOnMobile: boolean;
	hasLogoBarHeader: boolean;
	hasCastingButton: boolean;
	isUserConnected: boolean;
	stripeId: string | null;
	isHelpLinkDisplayed: boolean;
	children?: ReactNode;
}

/* eslint-disable complexity */
const WebPlayerLayout: React.FC<Props> = ({
	children,
	isPlayerHiddenOnMobile,
	hasLogoBarHeader,
	hasCastingButton,
	isUserConnected,
	stripeId,
	isHelpLinkDisplayed,
}) => {
	const dispatch = useDispatch();

	const selectedSong = useCurrentSongSelector();
	const castingSessionId = useSelector(castingSessionIdSelector);
	const waitingServiceWorker: ServiceWorker | null = useSelector(
		waitingServiceWorkerSelector,
	);

	const {
		isTrialEndDisplayed,
		hideTrialEnd,
		isTrialOngoingDisplayed,
		hideTrialOngoing,
	} = useFreeTrialState(castingSessionId);

	// TODO: extract to custom hook to reduce component size
	const [isNewAppBannerVisible, setIsNewAppBannerVisible] = useState(false);
	useEffect(() => {
		setIsNewAppBannerVisible(!!waitingServiceWorker);
	}, [waitingServiceWorker]);

	const [offersMessageAndModalResponse, fetchOffersMessageAndModal] =
		useAsyncFn(getOffersMessageAndModal, [], { loading: true });

	const isPlaying = !!selectedSong;
	const isCastingRemoteDevice = !!castingSessionId;

	const isDesktop =
		useMedia(`(min-width: ${breakpoints.wideDevices})`) && !isMobile();
	const [height, setHeight] = useState(window.innerHeight);
	window.onresize = debounce(() => {
		setHeight(window.innerHeight);
	}, 500);

	useEffect(() => {
		fetchOffersMessageAndModal();
	}, [fetchOffersMessageAndModal]);

	// TODO: extract to custom hook to reduce component size
	useEffect(() => {
		const message = offersMessageAndModalResponse.value?.message;

		if (isOngoingOfferWithExpirationDate(message)) {
			const remainingTime = getRemainingTimeInMilliseconds(
				message.expirationDatetime,
			);

			if (remainingTime > MAX_TIMEOUT_DELAY_IN_MILLISECONDS) {
				// Do not set the timeout if remaining time is too high because :
				// 1. it is very unlikely that a session stays open for a very long time
				// 2. too large values can break the timeout and act like 0 values
				return;
			}

			const timer = setTimeout(async () => {
				// get new message and modal after offer expires
				await fetchOffersMessageAndModal();

				// stop ongoing song
				dispatch(saveCurrentSong(null));
				if (castingSessionId) {
					postCastingEvent(castingSessionId, PlayerEvent.SELECT_SONG, "");
				}
			}, remainingTime);

			return clearTimeout(timer);
		}
	}, [
		castingSessionId,
		dispatch,
		fetchOffersMessageAndModal,
		offersMessageAndModalResponse.value,
	]);

	return (
		<LayoutContainer height={height}>
			{!isDesktop && (
				<Header
					hasLogoBarHeader={hasLogoBarHeader}
					isHelpLinkDisplayed={isHelpLinkDisplayed}
				/>
			)}
			<PlayerAreaContainer>
				<StyledB2cPlayerArea
					isCastingRemoteDevice={isCastingRemoteDevice}
					song={selectedSong}
					castingSessionId={castingSessionId}
					controls={true}
					stripeId={stripeId}
					isHidden={isPlayerHiddenOnMobile}
					isPlaying={isPlaying}
					offersMessageConfig={offersMessageAndModalResponse.value?.message}
				/>
				{isDesktop && (
					<DesktopModal
						offersModalConfig={offersMessageAndModalResponse.value?.modal}
						isTrialOngoingDisplayed={isTrialOngoingDisplayed}
						hideTrialOngoing={hideTrialOngoing}
					/>
				)}
			</PlayerAreaContainer>
			<NavigationArea>
				{isDesktop && (
					<Header
						hasLogoBarHeader={hasLogoBarHeader}
						isHelpLinkDisplayed={isHelpLinkDisplayed}
					/>
				)}
				<LayoutContent>{children}</LayoutContent>
				<CastingControlBar stripeId={stripeId} />
				{hasCastingButton && (
					<CastingButtonAndModal isUserConnected={isUserConnected} />
				)}
				<BannersContainer>
					{!isUserConnected && (
						<StyledCookieBanner isNewAppBannerVisible={isNewAppBannerVisible} />
					)}
					{waitingServiceWorker && isNewAppBannerVisible && (
						<NewAppBanner
							waitingServiceWorker={waitingServiceWorker}
							setIsBannerVisible={setIsNewAppBannerVisible}
						/>
					)}
				</BannersContainer>
				<BottomNavBar />
				{!isDesktop && (
					<MobileModal
						isTrialEndDisplayed={isTrialEndDisplayed}
						hideTrialEnd={hideTrialEnd}
					/>
				)}
			</NavigationArea>
		</LayoutContainer>
	);
};
export default WebPlayerLayout;
